import {CSSProperties} from 'react';

import {css} from 'styled-components';

import {EStackDirection, TSpacingSize} from '../../types';

const column = css`
  display: flex;
  flex-direction: column;
`;

const row = css`
  display: flex;
  flex-direction: row;
`;

const flexAlign = (
  mainAxis: CSSProperties['justifyContent'],
  crossAxis?: CSSProperties['alignItems'],
  display?: 'flex' | 'inline-flex',
) => css`
  display: ${display ?? 'flex'};
  justify-content: ${mainAxis};
  align-items: ${crossAxis ?? mainAxis};
`;

type TStackDirection = keyof typeof EStackDirection;

type TStackProps = {
  direction?: TStackDirection;
  size?: TSpacingSize;
  align?: CSSProperties['alignItems'];
  justify?: CSSProperties['justifyContent'];
};

const stack = ({
  size = '0',
  direction = EStackDirection.COLUMN,
  align = 'start',
  justify = 'start',
}: TStackProps = {}) => css`
  ${({theme}) =>
    direction === EStackDirection.COLUMN
      ? css`
          ${column}
          ${flexAlign(align, justify)}
        gap: ${theme.spacing[`s${size}`]};
        `
      : css`
          ${row}
          ${flexAlign(justify, align)}
        gap: ${theme.spacing[`s${size}`]};
        `}
`;

// @supports for FireFox support
const scroll = ({
  isDark,
  isWide,
  isVisibleOnHover,
}: {isDark?: boolean; isWide?: boolean; isVisibleOnHover?: boolean} = {}) => css`
  @supports not (selector(::-webkit-scrollbar)) {
    padding-right: ${({theme}) => (isWide ? theme.spacing.s2 : theme.spacing.s1)};
    scrollbar-width: thin;
    scrollbar-color: ${({theme}) => (isDark ? theme.palette.grey600 : theme.palette.grey300)}
      transparent;
  }

  &::-webkit-scrollbar {
    background: transparent;
    width: ${({theme}) => (isWide ? theme.spacing.s2 : theme.spacing.s1)};
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    ${({theme}) => css`
      width: ${isWide ? theme.spacing.s2 : theme.spacing.s1};
      background: ${isVisibleOnHover
        ? 'transparent'
        : isDark
          ? theme.palette.grey600
          : theme.palette.grey300};
      border-radius: ${theme.borderRadius.br2};
    `}
  }

  &:hover {
    &::-webkit-scrollbar-thumb {
      ${({theme}) => css`
        background: ${isDark ? theme.palette.grey600 : theme.palette.grey300};
      `}
    }
  }
`;

export const layoutMixins = {scroll, stack, row, column, flexAlign} as const;
